/**
 * Reusable "Request approval" form. Submits a maker-checker request via
 * POST /api/approvals; the matching approver picks it up at the Approvals
 * page.
 *
 * Each detail page owns the entity context (dealerId / loanId / deviceId)
 * and the permission key. The form only collects:
 *   - the outer justification (≥20 chars, recorded on the approval row)
 *   - the inner action reason (≥10 chars, baked into the payload the
 *     executor will pass through to the underlying service)
 *
 * The caller passes a `payloadBuilder` that takes the inner reason and
 * returns the payload object. This keeps the inner-payload shape under
 * the caller's control (and out of this component).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ApiError,
  type Approval,
  type ApprovalPermissionKey,
  type RequestApprovalRequest,
} from "@dfp/api-client";
import { Button } from "./Button.js";
import { api } from "../lib/api.js";

export interface RequestApprovalFormProps {
  readonly permissionKey: ApprovalPermissionKey;
  /** Short human label for the button + form heading ("Suspend dealer", "Write off loan", ...). */
  readonly label: string;
  /**
   * Builds the inner payload from the action reason. The entity id (e.g.,
   * dealerId) is the caller's responsibility — it is closed over here.
   */
  readonly payloadBuilder: (innerReason: string) => Record<string, unknown>;
  /** Optional callback fired after a successful submit. */
  readonly onSubmitted?: (approval: Approval) => void;
  /** Style override for the trigger button. */
  readonly variant?: "primary" | "secondary" | "danger";
}

export function RequestApprovalForm({
  permissionKey,
  label,
  payloadBuilder,
  onSubmitted,
  variant = "secondary",
}: RequestApprovalFormProps): JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [outerReason, setOuterReason] = useState("");
  const [innerReason, setInnerReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: RequestApprovalRequest) => api.post<Approval>("/api/approvals", body),
    onSuccess: (approval) => {
      setOpen(false);
      setOuterReason("");
      setInnerReason("");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["approvals", "list"] });
      onSubmitted?.(approval);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Request rejected");
      } else {
        setError("Network error");
      }
    },
  });

  function submit(): void {
    setError(null);
    if (outerReason.trim().length < 20) {
      setError("Justification must be at least 20 characters.");
      return;
    }
    if (innerReason.trim().length < 10) {
      setError("Action reason must be at least 10 characters.");
      return;
    }
    mutation.mutate({
      permissionKey,
      reason: outerReason.trim(),
      payload: payloadBuilder(innerReason.trim()),
    });
  }

  if (!open) {
    return (
      <Button variant={variant} onClick={() => setOpen(true)}>
        Request approval
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
      <p className="text-xs font-medium text-slate-700">
        Request approval to {label}
        <code className="ml-2 rounded bg-slate-100 px-1 text-[10px] text-slate-500">
          {permissionKey}
        </code>
      </p>

      <label className="block">
        <span className="text-xs font-medium text-slate-700">
          Justification (why you're requesting; recorded on the approval) · ≥ 20 chars
        </span>
        <textarea
          value={outerReason}
          onChange={(e) => setOuterReason(e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </label>

      <label className="block">
        <span className="text-xs font-medium text-slate-700">
          Action reason (passed to the underlying service; ≥ 10 chars)
        </span>
        <textarea
          value={innerReason}
          onChange={(e) => setInnerReason(e.target.value)}
          rows={2}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </label>

      {error ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex gap-2">
        <Button
          variant={variant}
          onClick={submit}
          disabled={
            mutation.isPending || outerReason.trim().length < 20 || innerReason.trim().length < 10
          }
        >
          {mutation.isPending ? "Submitting…" : `Submit request`}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setOuterReason("");
            setInnerReason("");
            setError(null);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
